import { defineMessages } from 'react-intl'

/**
 * Canonical customer-facing labels for Luxury Escapes product verticals and
 * the LE program / partner-brand names. Keyed by camelCase identifiers so
 * config-driven surfaces can reference descriptors consistently in code.
 *
 * These are the standardised labels used across nav, footer, checkout, and
 * partner-brand surfaces — see `docs/product-taxonomy.md` for the full
 * glossary and guidance on adding new entries.
 *
 * Consumers call `intl.formatMessage` themselves, e.g.
 * `intl.formatMessage(productLabels.hotels)` or
 * `<FormattedMessage {...productLabels.hotels} />`.
 */
const productLabels = defineMessages({
  businessTravel: {
    id: '16puE9f3VvqdIi--y81T',
    defaultMessage: 'Business Travel',
    description: 'Config-driven label for the business travel link shown in shared navigation surfaces',
  },
  carHire: {
    id: '8ZwA9-ddQCEbp-_wRZZa',
    defaultMessage: 'Car Hire',
    description: 'Config-driven label for the car hire link shown in shared navigation, footer, or sidebar surfaces',
  },
  accommodation: {
    id: 'mVLgncSoKp8uitszEim1',
    defaultMessage: 'Accommodation',
    description: 'Config-driven label for accommodation as a cart or trip-planner item type, checkout bundle breakdown, and order summaries',
  },
  cruise: {
    id: '2uO1FQHYceKJz__Iw1cZ',
    defaultMessage: 'Cruise',
    description: 'Config-driven singular label for a cruise product row shown in the loyalty points earn breakdown table',
  },
  cruises: {
    id: 'gGAv1vuigbVBK6XhAtiL',
    defaultMessage: 'Cruises',
    description: 'Config-driven label for the cruises link shown in shared navigation, footer, or sidebar surfaces',
  },
  cudo: {
    id: 'Zwchbeoqwc48yfF2CWqO',
    defaultMessage: 'Cudo',
    description: 'Config-driven partner brand label for Cudo shown in footer or partner-brand surfaces',
  },
  cvfrHolidays: {
    id: '3ot9et7DkSsaJ1fWnd_e',
    defaultMessage: 'CVFR Holidays',
    description: 'Config-driven brand label for CVFR Holidays shown in logo, footer, or partner-brand surfaces',
  },
  deals: {
    id: 'o7y1P2ZJe2cveNsm8hIv',
    defaultMessage: 'Deals',
    description: 'Config-driven partner brand label for Deals shown in footer or partner-brand surfaces',
  },
  directMaldivesFlights: {
    id: 'KjN2E89ePMc3CPA4jN-Z',
    defaultMessage: 'Direct Maldives Flights',
    description: 'Config-driven label for the direct Maldives flights link shown in shared navigation surfaces',
  },
  experience: {
    id: 'EJnlHihWvTGFZS1bA1sr',
    defaultMessage: 'Experience',
    description: 'Config-driven singular label for an experience product row shown in the loyalty points earn breakdown table',
  },
  experiences: {
    id: 'kGv7JMD3Z3LNdQCA6K9r',
    defaultMessage: 'Experiences',
    description: 'Config-driven label for the experiences link shown in shared navigation, footer, or sidebar surfaces',
  },
  flight: {
    id: 'Vjsk1W1Xf2kkhhK4BhrF',
    defaultMessage: 'Flight',
    description: 'Config-driven singular label for a flight product row shown in the loyalty points earn breakdown table',
  },
  flights: {
    id: 'rjkAgXNqcXA47CAL9sAD',
    defaultMessage: 'Flights',
    description: 'Config-driven label for the flights link shown in shared navigation, footer, or sidebar surfaces',
  },
  giftCards: {
    id: 'Fdyo8GZ5KQkWvc75vhh_',
    defaultMessage: 'Gift Cards',
    description: 'Config-driven label for the gift cards link shown in shared navigation, footer, or sidebar surfaces',
  },
  golf: {
    id: 'nuEE-728_wfga96BmVdb',
    defaultMessage: 'Golf',
    description: 'Config-driven label for the golf link shown in shared navigation surfaces',
  },
  groups: {
    id: 'a-Ry45gISOQOTFqvRBKz',
    defaultMessage: 'Groups',
    description: 'Config-driven label for the groups link shown in shared navigation or footer surfaces',
  },
  holidays: {
    id: 'mpbKIx7c9XM79v5UeRYu',
    defaultMessage: 'Holidays',
    description: 'Config-driven label for the holidays link shown in shared navigation, footer, or sidebar surfaces',
  },
  hotel: {
    id: '4y8ctsZ46VsZDgOw8Efu',
    defaultMessage: 'Hotel',
    description: 'Config-driven singular label for a hotel product or trip-planner booking type',
  },
  home: {
    id: 'j-noXtNNi7Kqihb9Fr1h',
    defaultMessage: 'Home',
    description: 'Config-driven label for the home link shown in shared navigation, footer, or sidebar surfaces',
  },
  homesAndVillas: {
    id: '9FgjGNcJvHHrI0F8fEDp',
    defaultMessage: 'Homes & Villas',
    description: 'Config-driven label for the homes and villas link shown in shared navigation surfaces',
  },
  hotels: {
    id: 'Du7wu76EAhcUNe7aYRrW',
    defaultMessage: 'Hotels',
    description: 'Config-driven label for the hotels link shown in shared navigation, footer, or sidebar surfaces',
  },
  hotelsAndResorts: {
    id: 'hlNqY6uTwK8zCnHbuT-t',
    defaultMessage: 'Hotels & Resorts',
    description: 'Config-driven label for the hotels and resorts link shown in shared navigation or sidebar surfaces',
  },
  inspiration: {
    id: 'SCxm4dKRDfsQ5WcVPKtr',
    defaultMessage: 'Inspiration',
    description: 'Config-driven label for the inspiration link shown in shared navigation surfaces',
  },
  leAgentHub: {
    id: 'YCr-jL_qfc-yJKZii6NH',
    defaultMessage: 'LE Agent Hub',
    description: 'Config-driven brand label for LE Agent Hub shown in logo or partner-brand surfaces',
  },
  luxuryEscapes: {
    id: '5turM7CS4vZ9TvX2xPHc',
    defaultMessage: 'Luxury Escapes',
    description: 'Config-driven brand label for Luxury Escapes shown in logo, footer, or partner-brand surfaces',
  },
  luxPlus: {
    id: 't1VsvOs4hSp9vtiZ85XS',
    defaultMessage: 'LuxPlus+',
    description: 'Config-driven label for the LuxPlus+ membership program shown in shared navigation or account surfaces',
  },
  maldivesBundles: {
    id: 'YVAOYUDUw_0ZiP7xa-Fs',
    defaultMessage: 'Maldives Bundles',
    description: 'Config-driven label for the Maldives bundles campaign link shown in shared navigation surfaces',
  },
  more: {
    id: 'iogTL25pepF33WNv_kzn',
    defaultMessage: 'More',
    description: 'Config-driven label for the more dropdown shown in shared navigation surfaces',
  },
  myEscapes: {
    id: 'H01E5qMD_amz1GJla5s9',
    defaultMessage: 'My Escapes',
    description: 'Navigation label for My Escapes section in the site header',
  },
  purchaseCredits: {
    id: 'vo5zMX4Ce57R4qn3g5D5',
    defaultMessage: 'Purchase Credits',
    description: 'Config-driven label for the purchase credits link shown in shared navigation surfaces for spoof-mode users',
  },
  referAFriend: {
    id: '1ibiA1tK4QuLynM1avt9',
    defaultMessage: 'Refer a Friend',
    description: 'Config-driven label for the refer-a-friend link shown in shared navigation surfaces',
  },
  reviewAndPay: {
    id: 'untLzd7mr1r9ByuyOBhu',
    defaultMessage: 'Review & Pay',
    description: 'Config-driven label for the review-and-pay step shown in checkout progress and single-step summary surfaces',
  },
  scoopon: {
    id: '2U2DxbcGZdlBbHSmq7_M',
    defaultMessage: 'Scoopon',
    description: 'Config-driven partner brand label for Scoopon shown in footer or partner-brand surfaces',
  },
  shop: {
    id: 'h7dyYt-ykomT2vTkJyCO',
    defaultMessage: 'Shop',
    description: 'Config-driven label for the shop or marketplace link shown in shared navigation surfaces',
  },
  societe: {
    id: 'yUDJ6xWCQUgwyOFcurqS',
    defaultMessage: 'Société',
    description: 'Config-driven label for the Société loyalty program shown in shared navigation surfaces',
  },
  support: {
    id: '9QaG60nE3rpbHlBuAlFK',
    defaultMessage: 'Support',
    description: 'Config-driven label for the support link shown in shared navigation, footer, or sidebar surfaces',
  },
  tour: {
    id: 'S4r_ssKptdgBBMwJCiu0',
    defaultMessage: 'Tour',
    description: 'Config-driven singular label for a tour product row shown in the loyalty points earn breakdown table',
  },
  tours: {
    id: 'yOTXLZhiQJuObKxhBiJI',
    defaultMessage: 'Tours',
    description: 'Config-driven label for the tours link shown in shared navigation, footer, or sidebar surfaces',
  },
  toursAndCruises: {
    id: 'PQNDf9pmC-Df0AIS_TSF',
    defaultMessage: 'Tours & Cruises',
    description: 'Config-driven label for the combined tours and cruises link shown in shared navigation surfaces',
  },
  transfersAndCars: {
    id: '4858klKHNvbzcJWrf3wV',
    defaultMessage: 'Transfers & Cars',
    description: 'Shared navigation and search label for airport transfers and car hire (rental cars), not vehicle sales',
  },
  travelProtection: {
    id: 'qnb-VRE3XIXz11_ZWiFY',
    defaultMessage: 'Travel Protection',
    description: 'Config-driven label for the travel protection link shown in shared navigation surfaces',
  },
  travelshoot: {
    id: 'wR5CkMsrZ7fHOEvaoVXj',
    defaultMessage: 'Travelshoot',
    description: 'Config-driven partner brand label for Travelshoot shown in footer or partner-brand surfaces',
  },
  treatme: {
    id: 'Xh20pzxYOzWaJyyHpS63',
    defaultMessage: 'Treatme',
    description: 'Config-driven brand label for Treatme shown in logo, footer, or partner-brand surfaces',
  },
  treatMe: {
    id: 'x8tkc_CPqs1vW84wWzN0',
    defaultMessage: 'Treat Me',
    description: 'Config-driven partner brand label for Treat Me shown in footer or partner-brand surfaces',
  },
  ultraLux: {
    id: 'J7UdomXHkCph_5dLGjHm',
    defaultMessage: 'Ultra Lux',
    description: 'Config-driven label for the Ultra Lux product link shown in shared navigation surfaces',
  },
  visitUsInStore: {
    id: 'OJY-V8ElE34mYkQ3bxv6',
    defaultMessage: 'Visit Us In Store',
    description: 'Config-driven label for the in-store visit link shown in shared navigation surfaces',
  },
  weddings: {
    id: 'NacytM3o5VYlYkVIKrLJ',
    defaultMessage: 'Weddings',
    description: 'Config-driven label for the weddings link shown in shared navigation or footer surfaces',
  },
})

/** Header logo screen-reader label: one `productLabels` entry per `config.BRAND`. */
export const navBrandLogoProductLabel = {
  luxuryescapes: productLabels.luxuryEscapes,
  cudotravel: productLabels.cudo,
  scoopontravel: productLabels.scoopon,
  treatmetravel: productLabels.treatme,
  dealstravel: productLabels.deals,
  leagenthub: productLabels.leAgentHub,
  leagenthub_cvfr: productLabels.cvfrHolidays,
} satisfies Record<App.BrandName, (typeof productLabels)[keyof typeof productLabels]>

export default productLabels
